import { useSignOut } from "@features/authentication/mutations";
import {
  DeleteAccountSection,
  HideUnlessLikedToggle,
  ProfileEditForm,
  ProfileHeader,
  ProfileInfo,
  ProfileNotFound,
} from "@features/profile/components";
import { useUpdateProfile } from "@features/profile/mutations/useUpdateProfile";
import type { User } from "@fitties/shared";
import { useLayoutContext } from "@router/layouts/MainLayout";
import { PageLoading } from "@shared/components/PageLoading";
import VersionStamp from "@shared/components/VersionStamp";
import useCurrentUser from "@shared/queries/useCurrentUser";
import { calculateAge } from "@shared/utils/ageUtils";
import clsx from "clsx";
import { LogOut, ScanEye, Shield } from "lucide-react";
import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

function ageFromDateOfBirth(dateOfBirth: Date | undefined): number | null {
  if (!dateOfBirth) {
    return null;
  }
  return calculateAge(dateOfBirth);
}

function ProfileEditPanel({
  user,
  formRef,
  onSuccess,
}: {
  user: User;
  formRef: RefObject<HTMLFormElement | null>;
  onSuccess: () => void;
}) {
  return (
    <div className="overflow-hidden">
      <div className="bg-white p-6">
        <ProfileEditForm
          bio={user.bio}
          {...(user.bioContent ? { bioContent: user.bioContent } : {})}
          desiredGenders={user.desired_genders}
          photoUrl={user.photo_url}
          includeInWeeklyPool={user.includeInWeeklyPool !== false}
          onSuccess={onSuccess}
          formRef={formRef}
        />
      </div>
      <div className="border-t border-gray-200 bg-white p-6">
        <DeleteAccountSection />
      </div>
    </div>
  );
}

/**
 * Profile page component for viewing and editing user profile.
 *
 * Displays user profile information and allows editing of profile fields.
 * Uses view/edit mode toggle to switch between display and edit states.
 *
 * @example
 * ```tsx
 * <ProfilePage />
 * ```
 */
export default function ProfilePage() {
  const { user, isLoading, error } = useCurrentUser();
  const [isEditMode, setIsEditMode] = useState(false);
  const { mutate: signOut, isPending: isSigningOut } = useSignOut();
  const { mutate: updateProfile, isPending: isUpdatingProfile } = useUpdateProfile();
  const editFormRef = useRef<HTMLFormElement | null>(null);
  const { setHeader, openAskForAngela } = useLayoutContext();

  const handleCancelEdit = useCallback(() => setIsEditMode(false), []);

  useEffect(() => {
    if (isEditMode) {
      setHeader({
        title: "Edit your profile",
        variant: "back",
        onBack: handleCancelEdit,
        onSave: () => editFormRef.current?.requestSubmit(),
      });
      return () => setHeader(null);
    }
    return setHeader(null);
  }, [isEditMode, setHeader, handleCancelEdit]);

  // Error state
  if (error) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50 p-8">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Failed to load profile</h2>
          <p className="text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  // No user state (finished loading without a profile)
  if (!isLoading && !user) {
    return <ProfileNotFound />;
  }

  const age = ageFromDateOfBirth(user?.date_of_birth);

  // Handle edit mode toggle
  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleSaveSuccess = () => {
    setIsEditMode(false);
  };

  const hideUnlessLiked = user?.hide_unless_liked === true;
  const toggleDisabled = isLoading || !user || isUpdatingProfile;

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      {/* Scrolls inside `<main>` so content clears the tab bar (MainLayout). Capped column below. */}
      <div className="mx-auto max-w-2xl">
        {isEditMode && user ? (
          <ProfileEditPanel user={user} formRef={editFormRef} onSuccess={handleSaveSuccess} />
        ) : (
          <div className="space-y-6 p-6">
            {isLoading || !user ? (
              <PageLoading message="Loading profile..." />
            ) : (
              <>
                {/* `space-y-6` stacks these blocks; padded wrapper avoids breaking parent `space-y` (single child rule). */}
                <ProfileHeader displayName={user.display_name} photoUrl={user.photo_url} />

                <div className="flex flex-row items-stretch gap-3">
                  <Link
                    to="/profile/preview"
                    className={clsx(
                      // Base layout & dimensions
                      "flex min-h-10 min-w-0 flex-1 flex-col justify-center gap-0.5 px-3 py-2 text-left",
                      // Typography & text styling
                      "font-mono",
                      // Visual appearance & backgrounds
                      "rounded-button border-2 border-[var(--color-border-card)] bg-white",
                      // User interaction states
                      "hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                      // Transitions
                      "transition-colors"
                    )}
                    data-testid="profile-preview-link"
                  >
                    <span
                      className={clsx(
                        // Base layout & dimensions
                        "flex max-h-5 min-w-0 flex-wrap items-center justify-center gap-2 overflow-hidden",
                        // Typography & text styling
                        "text-sm font-medium leading-none text-gray-900"
                      )}
                    >
                      <span className="inline-flex shrink-0 items-center gap-2">
                        <ScanEye
                          className="pointer-events-none h-4 w-4 shrink-0 -translate-y-px"
                          strokeWidth={2}
                          aria-hidden
                        />
                        Preview
                      </span>
                      <span className="shrink-0">Profile</span>
                    </span>
                  </Link>
                  <button
                    type="button"
                    onClick={handleEditClick}
                    className={clsx(
                      // Base layout & dimensions
                      "flex min-h-10 min-w-0 flex-1 items-center justify-center rounded-lg px-3 py-2",
                      // Typography & text styling
                      "font-mono text-sm font-medium text-white",
                      // Visual appearance & backgrounds
                      "bg-pink-600",
                      // User interaction states
                      "hover:bg-pink-700",
                      // Transitions
                      "transition-colors"
                    )}
                    data-testid="edit-profile-button"
                  >
                    Edit Profile
                  </button>
                </div>

                <div className="overflow-hidden rounded-lg bg-white shadow-sm">
                  <div className="p-6">
                    <ProfileInfo
                      displayName={user.display_name}
                      bio={user.bio}
                      bioContent={user.bioContent}
                      age={age}
                      gender={user.gender}
                      desiredGenders={user.desired_genders}
                    />
                  </div>
                </div>
              </>
            )}

            <div className="overflow-hidden rounded-lg bg-white shadow-sm">
              <div className="p-6">
                <HideUnlessLikedToggle
                  checked={hideUnlessLiked}
                  disabled={toggleDisabled}
                  onChange={(next) => updateProfile({ hide_unless_liked: next })}
                />
              </div>
            </div>

            {!isLoading && user ? (
              <div className="flex flex-col items-center gap-3 pt-2">
                <VersionStamp />
                <button
                  type="button"
                  onClick={openAskForAngela}
                  className={clsx(
                    "inline-flex h-10 items-center gap-2 px-4",
                    "font-mono text-sm font-medium leading-none text-gray-900",
                    "rounded-button border-2 border-[var(--color-border-card)] bg-white",
                    "hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                    "transition-colors"
                  )}
                  data-testid="ask-for-angela-profile-button"
                >
                  <Shield className="pointer-events-none h-4 w-4 shrink-0 text-pink-600" strokeWidth={2} aria-hidden />
                  Ask for Angela
                </button>
                <button
                  type="button"
                  onClick={() => signOut()}
                  disabled={isSigningOut}
                  className={clsx(
                    // Base layout & dimensions
                    "inline-flex h-10 items-center gap-2 px-4",
                    // Typography & text styling
                    "font-mono text-sm font-medium leading-none text-gray-900",
                    // Visual appearance & backgrounds
                    "rounded-button border-2 border-[var(--color-border-card)] bg-white",
                    // User interaction states
                    "hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                    // Disabled state styling
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    // Transitions
                    "transition-colors"
                  )}
                  data-testid="signout-button"
                >
                  <LogOut className="pointer-events-none h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
                  {isSigningOut ? "Logging Out…" : "Log Out"}
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
